import {
  CURRENT_PRICING_VERSION,
  IPlanPriceV3,
  IPlanV3,
  plansV3,
  StripePricesLookupKeys,
  currencyFormatter,
} from "shared";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router";

import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Heading } from "../../../common/Atoms/Typography/Heading";
import { SmallSpinner } from "../../../common/Atoms/SmallSpinner";
import { Select } from "../../../common/Atoms/Select";
import { Accordian } from "../../../common/Atoms/Accordian";
import { usePlans } from "../../hooks/usePlans";
import { useSubscriptions } from "../../hooks/useSubscriptions";

import { NewPlan } from "./NewPlan";

const currencyFlags: Record<string, string> = {
  USD: `🇺🇸`,
  AUD: `🇦🇺`,
  CAD: `🇨🇦`,
  EUR: `🇪🇺`,
  GBP: `🇬🇧`,
  NZD: `🇳🇿`,
  ZAR: `🇿🇦`,
};

export function NewPlans({ inModal = false }: { inModal?: boolean }) {
  const { data: plans, isLoading } = usePlans();
  const { data: subscriptions, activeSubscriptionsCurrency } = useSubscriptions();
  const id = Number(useParams().subscriptionId);
  const lockedCurrency = activeSubscriptionsCurrency?.toUpperCase();
  const [selectedCurrency, setSelectedCurrency] = useState(lockedCurrency || `USD`);
  const [faqOpen, setFaqOpen] = useState(false);

  useEffect(() => {
    if (lockedCurrency && selectedCurrency !== lockedCurrency) {
      setSelectedCurrency(lockedCurrency);
    }
  }, [lockedCurrency, selectedCurrency]);

  const subscription = useMemo(() => {
    return subscriptions.find(s => s.id === id);
  }, [ subscriptions]);

  const filteredPlans = useMemo(() => {
    const currentPlan = subscription?.mainLookupKey as StripePricesLookupKeys;

    return Object.entries(plansV3).reduce((acc, [key, product]) => {
      const filteredPrices: {
        StripePricesLookupKeys?: IPlanPriceV3;
      } = {};
      for (const [priceKey, price] of Object.entries(product.prices)) {
        if (
          price.version === CURRENT_PRICING_VERSION &&
          priceKey !== StripePricesLookupKeys.Essentials50 &&
          !price.hidden
        ) {
          filteredPrices[priceKey] = price;
        }
        else if (priceKey === currentPlan) {
          // Always add the current plan
          filteredPrices[priceKey] = price;
        }
      }

      acc[key] = { ...product, prices: filteredPrices };

      return acc;
    }, {} as typeof plansV3);
  }, [subscriptions]);

  const availableCurrencies = useMemo(() => {
    if (lockedCurrency) {
      return [lockedCurrency];
    }

    const currencies = new Set<string>([`USD`]);

    plans.forEach(p => {
      Object.keys(p.currency_options || {}).forEach(code => {
        currencies.add(code.toUpperCase());
      });
    });

    return Array.from(currencies).sort((a, b) => {
      if (a === `USD`) return -1;
      if (b === `USD`) return 1;

      return a.localeCompare(b);
    });
  }, [plans, lockedCurrency]);

  const currencyOptions = useMemo(() => {
    return availableCurrencies.map(code => {
      const flag = currencyFlags[code];

      return {
        value: code,
        label: flag ? `${flag} ${code}` : code,
      };
    });
  }, [availableCurrencies]);

  const addOnFaqPrice = useMemo(() => {
    const addOnLookupPriority = [
      StripePricesLookupKeys.OrgAddOnMultiCurrency2026,
      StripePricesLookupKeys.OrgAddOn29,
      StripePricesLookupKeys.OrgAddOn,
    ];
    const formatCurrency = currencyFormatter(selectedCurrency);

    for (const lookupKey of addOnLookupPriority) {
      const stripePrice = plans.find(p => p.lookup_key === lookupKey);
      const selectedOption = stripePrice?.currency_options?.[selectedCurrency.toLowerCase()];
      const unitAmount = selectedOption?.unit_amount ?? stripePrice?.unit_amount;

      if (unitAmount != null) {
        return formatCurrency(unitAmount / 100);
      }
    }

    return formatCurrency(29);
  }, [plans, selectedCurrency]);

  if (isLoading) return (
    <div className={ `flex justify-center pt-6` }>
      <SmallSpinner
        colored
        size={ `lg` }
      />
    </div>
  );

  return (
    <div className={ `w-full` }>
      <div className={ `mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-6` }>
        {
          !lockedCurrency && availableCurrencies.length > 1 && (
            <div className={ `mb-4 flex justify-end` }>
              <div className={ `w-36` }>
                <Select
                  options={ currencyOptions }
                  label={ `` }
                  selected={ selectedCurrency }
                  onChange={ e => setSelectedCurrency(String(e.value)) }
                  size={ `sm` }
                />
              </div>
            </div>
          )
        }
        <div className={ `grid grid-cols-1 md:grid-cols-2 gap-6` }>
          {
            Object.entries(filteredPlans).map(([key, product]) => (
              <NewPlan
                key={ key }
                plan={ product as IPlanV3 }
                selectedCurrency={ selectedCurrency }
              />
            ))
          }
        </div>
      </div>
      <div className={ `flex justify-center pt-4` }>
        <Paragraph
          className={ `text-center text-gray-500 italic` }
          variant={ `help` }>
          { `All prices in ${selectedCurrency}. Price excludes any taxes if applicable. Billed Monthly. Cancel Anytime.` }
        </Paragraph>
      </div>
      <div className={ `flex justify-center pt-1` }>
        <Paragraph
          className={ `text-center text-gray-500 italic` }
          variant={ `help` }>
          { `*Invoices means number of invoices we needed to track. E.g Only unpaid invoices in most cases.` }
        </Paragraph>
      </div>
      {
        inModal ?
          (
            <div className={ `mx-auto max-w-3xl px-4 pt-8` }>
              <Accordian
                title={ `F.A.Qs` }
                open={ faqOpen }
                setOpen={ () => setFaqOpen(!faqOpen) }
              >
                <div className={ `space-y-3 text-gray-700 pt-3` }>
                  { renderFaqItems() }
                </div>
              </Accordian>
            </div>
          ) :
          (
            <div className={ `flex justify-center pt-12` }>
              <div className={ `flex flex-col items-center` }>
                <Heading
                  size={ `3xl` }
                  variant={ `secondary` }
                >
                  { `F.A.Qs` }
                </Heading>
                <div className={ `space-y-3 max-w-3xl px-4 text-gray-700` }>
                  { renderFaqItems() }
                </div>

              </div>
            </div>
          )
      }
    </div>
  );

  function renderFaqItems() {
    return (
      <>
        { /* <Heading>
          { `What is counted as an action?` }
        </Heading>
        <Paragraph>
          { `An action is when we issue a late fee, remove a discount or send an SMS message` }
        </Paragraph> */ }

        <Heading>
          { `What is counted an email?` }
        </Heading>
        <Paragraph>
          { `An email is when we send an auto-statement, reminder, late fee & discount notification emails` }
        </Paragraph>

        <Heading>
          { `What if I have more than one organisation?` }
        </Heading>
        <Paragraph>
          { `Multiple organisations can be added to any Pro plan to share the allowance for just ${addOnFaqPrice} per month each. Please contact us if you wish to remove any add ons` }
        </Paragraph>

        <Heading>
          { `Is there also tax to pay on the subscription price?` }
        </Heading>
        <Paragraph>
          { `Paidnice will add any applicable taxes to your monthly subscription. Paidnice is required to collect New Zealand GST for any customer with their billing address in New Zealand. ` }
        </Paragraph>
      </>
    );
  }
}
